// web/src/lib/authMode.ts
// 运行时鉴权模式:
/**
 * - `local`:本地账号 + OAuth 全部可用(默认)
 * - `oauth_only`:禁用本地登录/注册,只允许 OAuth2;前端隐藏本地表单,
 *   后端在 auth.rs 中独立拦截(不要只靠前端)。
 *
 * 设计原则:
 *   1. UI 默认值来自构建时 `import.meta.env.VITE_AUTH_MODE_BUILTIN`(由
 *      vite.config.ts 通过 .env.<mode> 注入),确保首屏渲染不需要等接口。
 *   2. 启动后调 `GET /api/v1/auth/mode` 拉取运行时真值,覆盖构建时值。
 *      后端才是权威,UI 只是展示。
 *   3. 接口失败/超时时保持当前缓存值(通常是构建时值),不要切回 local,
 *      避免 prod 环境网络抖动把"已禁用"的本地登录放开。
 */
import { apiClient } from '@/api/client';

export type AuthMode = 'local' | 'oauth_only';

let cached: AuthMode | null = null;
let inflight: Promise<AuthMode> | null = null;

function readBuiltin(): AuthMode {
  const v = (import.meta.env.VITE_AUTH_MODE_BUILTIN as string | undefined) ?? 'local';
  return v === 'oauth_only' ? 'oauth_only' : 'local';
}

/** 同步读取(首屏渲染用)。永远返回非空值,默认 = 打包时的 VITE_AUTH_MODE。 */
export function getAuthMode(): AuthMode {
  return cached ?? readBuiltin();
}

/** 异步刷新(应用启动时调一次)。失败时保持当前值,不打回 local。 */
export async function refreshAuthMode(): Promise<AuthMode> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await apiClient.get<{ mode: AuthMode }>('/v1/auth/mode', {
        // 端点不鉴权,但客户端全局拦截器不会拦它;为防 token 失效时 retry 风暴,
        // 显式低重试 / 短超时:
        timeout: 5_000,
      });
      if (data && (data.mode === 'oauth_only' || data.mode === 'local')) {
        cached = data.mode;
      }
      console.log('[gitman-dbg] GET /v1/auth/mode ->', JSON.stringify(data), 'cached now=', cached);
    } catch (e) {
      // 静默:网络或后端不可用时保留当前值(默认 oauth_only,符合 prod 期望)
      console.warn('[gitman-dbg] GET /v1/auth/mode failed, keep cached=', cached, 'reason:', (e as Error)?.message, e);
    } finally {
      inflight = null;
    }
    return cached ?? readBuiltin();
  })();
  return inflight;
}

/** 主要供测试 / 设置页改完后用,主动把缓存置空让下一次 readBuiltin 生效。 */
export function resetAuthModeCache(): void {
  cached = null;
}
